import React from 'react';
import TranslationsContext from 'providers/translationsProvider';
import instance from 'services/api';
import { message } from 'antd';
import NoConnectionMessage from 'components/elements/NoConnectionMessage';
import { captureException } from '@sentry/browser';
import { isHandledStatus, isSilent } from 'services/api/toastConfig';

const NO_ERR = null;
const MESSAGES_DELAY = 1.5;

const isBridgeRequest = config =>
  config?.baseURL?.includes('bridgeforbillions') ||
  config?.baseURL?.includes('bfb') ||
  config?.baseURL?.includes('localhost');

const isMutationRequest = config => config?.method === 'patch' || config?.method === 'post';

const isNotLoginPage = () => window.location.pathname.indexOf('login') === -1;

const shouldShowToast = config => !isSilent(config);

/**
 * ApiMessageHandler - Handles API toast notifications and error messages.
 * @see src/services/api/TOAST_CONFIG.md for usage documentation.
 */
export default function ApiMessageHandler() {
  const { t } = React.useContext(TranslationsContext);
  const [err, setError] = React.useState(null);
  const [announcement, setAnnouncement] = React.useState('');

  React.useEffect(() => {
    message.config({
      top: 12,
      maxCount: 1,
      duration: 2,
    });
  }, []);

  const DATA_NOT_SAVED = t('New data is not saved');
  const SERVER_ERR = t(
    'Please, refresh the page by clicking here and try again. If this problem persists, reach out to us via the chat.'
  );

  const announcementRef = React.useRef(null);

  const showToast = React.useCallback((type, content, duration = MESSAGES_DELAY) => {
    message[type](content, duration);
    if (announcementRef.current) clearTimeout(announcementRef.current);
    setAnnouncement('');
    announcementRef.current = setTimeout(() => setAnnouncement(content), 100);
  }, []);

  const errorMessageHandler = React.useCallback(
    error => {
      let errorMessage = null;
      const isNetworkError = error.message === 'Network Error';
      const isTimeout = error.code === 'ECONNABORTED';
      const showToastForRequest = shouldShowToast(error?.config);

      if (error?.response && isNotLoginPage()) {
        const responseError = error.response.data?.detail;
        const requestUrl = error.response.config?.url || '';
        const requestMethod = error.response.config?.method;

        const isPublicEndpoint =
          requestUrl.includes('/public_programs/') ||
          requestUrl.includes('/demo-projects/') ||
          requestUrl.includes('/customizations/');

        // The caller shows its own message for these statuses (see withHandledStatuses).
        const isHandledByCaller = isHandledStatus(error.response.config, error.response.status);

        if (responseError && !isPublicEndpoint && !isHandledByCaller) {
          if (requestMethod === 'post' && showToastForRequest) {
            errorMessage = t('An error occurred while submitting your data. Please try again.');
          } else if (requestMethod === 'patch' && showToastForRequest) {
            errorMessage = DATA_NOT_SAVED;
          } else if (requestMethod !== 'post' && requestMethod !== 'patch') {
            errorMessage = `Error: ${responseError}`;
          }
        }

        if (
          error.response.status > 499 &&
          !error.response.config.url.includes('/customizations/')
        ) {
          setError(SERVER_ERR);
        }
      }

      if (isNetworkError || isTimeout) {
        const messageText = isNetworkError
          ? t('Network problem! Please check your internet connection.')
          : t('Timeout! Please try again.');

        setError(SERVER_ERR);
        showToast('error', messageText, MESSAGES_DELAY * 2);

        captureException(error, {
          tags: {
            networkError: isNetworkError,
            timeout: isTimeout,
          },
          extra: {
            axiosConfig: error.config,
            axiosResponse: error.response?.data ?? null,
          },
        });
      }

      if (errorMessage) {
        showToast('error', errorMessage, MESSAGES_DELAY * 2);
      }

      throw error;
    },
    [showToast]
  );

  const responseMessageHandler = React.useCallback(
    response => {
      if (
        response?.status < 300 &&
        isBridgeRequest(response.config) &&
        isMutationRequest(response.config) &&
        isNotLoginPage() &&
        shouldShowToast(response.config)
      ) {
        setError(NO_ERR);
        setTimeout(() => {
          showToast('success', t('Data saved'));
        }, MESSAGES_DELAY / 2);
      }
      return response;
    },
    [showToast]
  );

  const requestMessageHandler = React.useCallback(
    config => {
      if (
        isBridgeRequest(config) &&
        isMutationRequest(config) &&
        isNotLoginPage() &&
        shouldShowToast(config)
      ) {
        if (navigator.onLine) {
          showToast('loading', t('Saving...'), MESSAGES_DELAY);
        } else {
          showToast('error', DATA_NOT_SAVED, MESSAGES_DELAY);
        }
      }
      return config;
    },
    [showToast]
  );

  React.useEffect(() => {
    const requestInterceptor = instance.interceptors.request.use(requestMessageHandler);
    const responseInterceptor = instance.interceptors.response.use(
      responseMessageHandler,
      errorMessageHandler
    );

    return () => {
      instance.interceptors.request.eject(requestInterceptor);
      instance.interceptors.response.eject(responseInterceptor);
      if (announcementRef.current) {
        clearTimeout(announcementRef.current);
      }
    };
  }, [requestMessageHandler, responseMessageHandler, errorMessageHandler]);

  if (err) {
    return (
      <>
        <NoConnectionMessage message={t(err)} t={t} />
        <span
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {announcement}
        </span>
      </>
    );
  }

  return (
    <span
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      {announcement}
    </span>
  );
}
